import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Shield, Lock, Server, Eye, Bell, FileCheck, AlertTriangle, CheckCircle, MapPin } from 'lucide-react';

const Security = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 pb-20">
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div>
              <div className="flex items-center space-x-3">
                <Shield className="w-8 h-8" />
                <h1 className="text-2xl font-bold">Security & Compliance</h1>
              </div>
              <p className="text-blue-100 mt-1">Last updated: January 19, 2025</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Security Overview */}
        <Card className="mb-6 border-2 border-green-500">
          <CardContent className="p-8">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-10 h-10 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Our Commitment to Security</h2>
                <p className="text-gray-700 leading-relaxed">
                  At Hines Time, security is our top priority. We implement industry-leading security practices 
                  to protect your data, ensure privacy, and maintain the highest standards of compliance. 
                  Your trust is earned through transparency and robust security measures.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Data Encryption */}
          <Card>
            <CardHeader className="bg-blue-50 border-b">
              <CardTitle className="flex items-center space-x-2">
                <Lock className="w-6 h-6 text-blue-600" />
                <span>Data Encryption</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Encryption in Transit:</strong>
                    <p className="text-sm text-gray-600">All data transmitted between your browser and our servers is encrypted using TLS 1.2+ (HTTPS) with 256-bit encryption.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Encryption at Rest:</strong>
                    <p className="text-sm text-gray-600">All data stored in our databases is encrypted using AES-256 encryption.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Password Security:</strong>
                    <p className="text-sm text-gray-600">Passwords are hashed using bcrypt with salt, making them impossible to reverse-engineer.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Infrastructure Security */}
          <Card>
            <CardHeader className="bg-purple-50 border-b">
              <CardTitle className="flex items-center space-x-2">
                <Server className="w-6 h-6 text-purple-600" />
                <span>Infrastructure Security</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Cloud Infrastructure:</strong>
                    <p className="text-sm text-gray-600">Hosted on enterprise-grade cloud infrastructure with 99.9% uptime SLA.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Regular Backups:</strong>
                    <p className="text-sm text-gray-600">Automated daily backups with 30-day retention and point-in-time recovery.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">DDoS Protection:</strong>
                    <p className="text-sm text-gray-600">Advanced DDoS mitigation and rate limiting to prevent abuse.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Access Control */}
          <Card>
            <CardHeader className="bg-green-50 border-b">
              <CardTitle className="flex items-center space-x-2">
                <Eye className="w-6 h-6 text-green-600" />
                <span>Access Control</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Role-Based Access:</strong>
                    <p className="text-sm text-gray-600">Granular permissions for Admin, Supervisor, and User roles.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Two-Factor Authentication:</strong>
                    <p className="text-sm text-gray-600">Optional 2FA via Microsoft Authenticator with backup codes for account recovery.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Biometric Authentication:</strong>
                    <p className="text-sm text-gray-600">Secure passwordless sign-in with Face ID, Touch ID, or fingerprint using WebAuthn standard.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Session Management:</strong>
                    <p className="text-sm text-gray-600">Secure JWT tokens with automatic expiration and refresh mechanisms.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Monitoring & Auditing */}
          <Card>
            <CardHeader className="bg-orange-50 border-b">
              <CardTitle className="flex items-center space-x-2">
                <Bell className="w-6 h-6 text-orange-600" />
                <span>Monitoring & Auditing</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Comprehensive Audit Logs:</strong>
                    <p className="text-sm text-gray-600">All actions are logged with timestamp, user, and details for compliance tracking.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Real-Time Monitoring:</strong>
                    <p className="text-sm text-gray-600">24/7 system monitoring with automated alerts for security incidents.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Time Entry Edits:</strong>
                    <p className="text-sm text-gray-600">Full audit trail of who edited time entries, when, and why.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Biometric Authentication Feature */}
        <Card className="mt-6 border-2 border-blue-500">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 border-b">
            <CardTitle className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-blue-600" />
              <span>Biometric Authentication (WebAuthn)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Hines Time supports passwordless biometric authentication using the WebAuthn standard. 
                This allows you to sign in securely using your device's built-in biometric sensors without 
                typing a password.
              </p>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-bold text-blue-900 mb-3">Supported Devices & Methods:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-blue-900">iPhone & iPad:</strong>
                      <p className="text-sm text-blue-700">Face ID or Touch ID</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-blue-900">Android Devices:</strong>
                      <p className="text-sm text-blue-700">Fingerprint or Face Unlock</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-blue-900">Mac Computers:</strong>
                      <p className="text-sm text-blue-700">Touch ID</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-blue-900">Windows Devices:</strong>
                      <p className="text-sm text-blue-700">Windows Hello (Face, Fingerprint, PIN)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-bold text-green-900 mb-2">Security Benefits:</h3>
                <ul className="space-y-2 text-sm text-green-800">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Phishing Resistant:</strong> Cannot be stolen or phished like passwords</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>No Password to Remember:</strong> Your face or fingerprint is your key</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Device-Specific:</strong> Credentials are stored securely on your device only</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Industry Standard:</strong> Uses W3C WebAuthn standard supported by major browsers</span>
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-2">How to Enable:</h3>
                <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
                  <li>Go to <strong>Settings</strong> page</li>
                  <li>Find the <strong>Biometric Authentication</strong> section</li>
                  <li>Click <strong>Add Device</strong></li>
                  <li>Follow the prompts to enroll your biometric</li>
                  <li>Use the "Sign in with Face ID / Touch ID" button on the login page</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance */}
        <Card className="mt-6">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 border-b">
            <CardTitle className="flex items-center space-x-2">
              <FileCheck className="w-6 h-6 text-blue-600" />
              <span>Compliance & Certifications</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-3">GDPR Compliance</h3>
                <p className="text-sm text-gray-700 mb-4">
                  We are fully compliant with the General Data Protection Regulation (GDPR) for European customers:
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li><CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />Right to access your data</li>
                  <li><CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />Right to data portability</li>
                  <li><CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />Right to be forgotten</li>
                  <li><CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />Data processing agreements available</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-3">Industry Standards</h3>
                <p className="text-sm text-gray-700 mb-4">
                  We adhere to internationally recognized security standards:
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li><CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />OWASP Top 10 security practices</li>
                  <li><CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />SOC 2 Type II compliant infrastructure</li>
                  <li><CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />PCI DSS compliant payments (via Stripe)</li>
                  <li><CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />ISO 27001 aligned practices</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Protection */}
        <Card className="mt-6">
          <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50 border-b">
            <CardTitle>Data Protection Measures</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-bold text-gray-900 mb-2">Multi-Tenant Data Isolation</h3>
                <p className="text-sm text-gray-700">
                  Each company's data is completely isolated in the database. Company A cannot access Company B's data under any circumstances. 
                  All queries are filtered by company_id to ensure strict data segregation.
                </p>
              </div>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-bold text-gray-900 mb-2">GPS Location Data</h3>
                <p className="text-sm text-gray-700">
                  Location data is collected only with user consent and browser permission. It is used solely for job site verification 
                  and payroll accuracy. Location data is only accessible to company administrators and supervisors for legitimate business purposes.
                </p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-bold text-gray-900 mb-2">Photo & Document Storage</h3>
                <p className="text-sm text-gray-700">
                  Job photos and toolbox PDFs are stored securely on our servers with access restricted to authorized users within the company. 
                  Files are scanned for malware and validated for type/size before storage.
                </p>
              </div>
              
              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="font-bold text-gray-900 mb-2">Payment Information</h3>
                <p className="text-sm text-gray-700">
                  We never store your credit card information. All payment processing is handled by Stripe, a PCI DSS Level 1 certified payment processor. 
                  We only store a secure token for subscription management.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Practices */}
        <Card className="mt-6">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b">
            <CardTitle>Security Practices</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-blue-600" />
                  Application Security
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Input validation on all forms</li>
                  <li>• SQL injection prevention</li>
                  <li>• XSS attack protection</li>
                  <li>• CSRF token validation</li>
                  <li>• Secure session management</li>
                  <li>• Rate limiting on API endpoints</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <Lock className="w-5 h-5 mr-2 text-green-600" />
                  Network Security
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Firewall protection</li>
                  <li>• Intrusion detection systems</li>
                  <li>• DDoS mitigation</li>
                  <li>• Network segmentation</li>
                  <li>• VPN access for admin operations</li>
                  <li>• Regular security patches</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-purple-600" />
                  Operational Security
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Employee background checks</li>
                  <li>• Security awareness training</li>
                  <li>• Principle of least privilege</li>
                  <li>• Regular security audits</li>
                  <li>• Incident response plan</li>
                  <li>• Disaster recovery procedures</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Incident Response */}
        <Card className="mt-6 border-2 border-orange-300">
          <CardHeader className="bg-orange-50 border-b">
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              <span>Incident Response</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-700 mb-4">
              In the unlikely event of a security incident, we have a comprehensive response plan:
            </p>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="font-bold text-orange-600 mr-3">1.</span>
                <div>
                  <strong>Immediate Detection:</strong> Automated monitoring systems detect and alert our team 24/7
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-orange-600 mr-3">2.</span>
                <div>
                  <strong>Rapid Response:</strong> Security team responds within 15 minutes to contain and investigate
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-orange-600 mr-3">3.</span>
                <div>
                  <strong>Customer Notification:</strong> Affected customers notified within 72 hours as required by GDPR
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-orange-600 mr-3">4.</span>
                <div>
                  <strong>Resolution & Prevention:</strong> Root cause analysis and implementation of preventive measures
                </div>
              </li>
              <li className="flex items-start">
                <span className="font-bold text-orange-600 mr-3">5.</span>
                <div>
                  <strong>Transparency:</strong> Post-incident report shared with affected parties
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Vulnerability Reporting */}
        <Card className="mt-6">
          <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 border-b">
            <CardTitle>Responsible Disclosure</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-700 mb-4">
              We welcome security researchers and users to report potential vulnerabilities responsibly.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <h4 className="font-bold text-blue-900 mb-2">Report a Security Issue:</h4>
              <p className="text-sm text-blue-800 mb-3">
                If you discover a security vulnerability, please email us at:
              </p>
              <p className="text-blue-900 font-mono bg-white px-3 py-2 rounded border border-blue-200 inline-block">
                security@hinestime.com
              </p>
              <p className="text-sm text-blue-800 mt-3">
                Please include: Description of the vulnerability, steps to reproduce, potential impact, and your contact information. 
                We commit to responding within 48 hours.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Third-Party Security */}
        <Card className="mt-6">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b">
            <CardTitle>Third-Party Service Security</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-700 mb-4">
              We carefully select third-party services that meet our security standards:
            </p>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">Stripe (Payment Processing)</h4>
                  <p className="text-sm text-gray-600">PCI DSS Level 1 certified, SOC 2 Type II compliant, handles billions in transactions annually</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Server className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">AWS (Email & SMS)</h4>
                  <p className="text-sm text-gray-600">AWS SES and SNS for notifications, ISO 27001 certified, GDPR compliant</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">OpenStreetMap (Geocoding)</h4>
                  <p className="text-sm text-gray-600">Open-source mapping, privacy-focused, no tracking cookies, GDPR compliant</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Best Practices for Users */}
        <Card className="mt-6 border-2 border-yellow-300">
          <CardHeader className="bg-yellow-50 border-b">
            <CardTitle className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-yellow-700" />
              <span>Security Best Practices for Users</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-700 mb-4">Help us keep your account secure:</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Use a strong, unique password (minimum 8 characters)</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Enable Two-Factor Authentication</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Never share your login credentials</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Log out on shared devices</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Keep your authenticator app secure</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Report suspicious activity immediately</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="mt-6 bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">Have Security Questions?</h3>
            <p className="text-blue-100 mb-6">
              Our security team is here to help. Contact us for security inquiries, 
              vulnerability reports, or to request our security whitepaper.
            </p>
            <div className="space-y-2">
              <p className="font-semibold">Security Team: security@hinestime.com</p>
              <p className="font-semibold">General Support: support@hinestime.com</p>
              <p className="text-sm text-blue-100 mt-4">
                Expected response time: Within 48 hours for security issues, 24 hours for critical vulnerabilities
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Security;
